import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper,
  CircularProgress,
  Divider,
  Chip,
  Avatar,
  List,
  ListItem,
  Tooltip
} from '@mui/material';
import MuiGrid from '../components/MuiGrid';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import StoreIcon from '@mui/icons-material/Store';
import GavelIcon from '@mui/icons-material/Gavel';
import GamesIcon from '@mui/icons-material/Games';
import type { League } from '../types/models';

const Leagues: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leagues, setLeagues] = useState<any[]>([]);

  useEffect(() => {
    fetchLeagues();
  }, []);

  const fetchLeagues = async () => {
    setLoading(true);
    try {
      // Fetch all leagues with club data, judge data and game counts
      const { data, error } = await supabase
        .from('leagues')
        .select(`
          *,
          club:club_id (
            id,
            name
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          ),
          games (
            id,
            winner
          )
        `)
        .order('name', { ascending: true });

      if (error) throw error;

      // Add game statistics to each league
      const leaguesWithStats = data?.map(league => {
        const games = league.games || [];
        const gameCount = games.length;
        const civilianWins = games.filter((g: any) => g.winner === 'CIVILIANS').length;
        const mafiaWins = games.filter((g: any) => g.winner === 'MAFIA').length;
        const tieGames = games.filter((g: any) => g.winner === 'TIE').length;
        
        return {
          ...league,
          gameCount,
          civilianWins,
          mafiaWins,
          tieGames
        };
      }) || [];

      setLeagues(leaguesWithStats);
    } catch (error: any) {
      console.error('Error fetching leagues:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLeagueClick = (leagueId: string) => {
    navigate(`/league/${leagueId}`);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Leagues
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper 
            elevation={3}
            sx={{ 
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {leagues.length > 0 ? (
              <List disablePadding>
                {leagues.map((league, index) => (
                  <React.Fragment key={league.id}>
                    <ListItem 
                      onClick={() => handleLeagueClick(league.id)}
                      sx={{ 
                        p: 0,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)'
                        }
                      }}
                    >
                      <Box 
                        sx={{ 
                          width: '100%',
                          py: 3,
                          px: 3,
                        }}
                      >
                        <MuiGrid container spacing={3} alignItems="center">
                          {/* League Name & Club */}
                          <MuiGrid item xs={12} md={5} lg={5}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 48, height: 48 }}>
                                <GamesIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="h6">
                                  {league.name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <StoreIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {league.club?.name || 'Unknown Club'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <GavelIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {league.judge?.nickname || 'Unknown Judge'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          </MuiGrid>
                          
                          {/* Games Count */}
                          <MuiGrid item xs={4} sm={4} md={2} sx={{ textAlign: 'center' }}>
                            <Box sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              height: '100%',
                              mx: "15px"
                            }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Games
                              </Typography>
                              <Typography variant="h5" fontWeight="medium">
                                {league.gameCount}
                              </Typography>
                            </Box>
                          </MuiGrid>
                          
                          {/* Civilian Wins */}
                          <MuiGrid item xs={4} sm={2} md={2} sx={{ textAlign: 'center' }}>
                            <Box sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              height: '100%',
                              mx: "15px"
                            }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Civilian Wins
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <Typography variant="h5" fontWeight="medium" color="primary.main">
                                  {league.civilianWins}
                                </Typography>
                                {league.gameCount > 0 && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    ({((league.civilianWins / league.gameCount) * 100).toFixed(0)}%)
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </MuiGrid>
                          
                          {/* Mafia Wins */}
                          <MuiGrid item xs={4} sm={2} md={2} sx={{ textAlign: 'center' }}>
                            <Box sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              height: '100%',
                              mx: "15px"
                            }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Mafia Wins
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <Typography variant="h5" fontWeight="medium" color="error.main">
                                  {league.mafiaWins}
                                </Typography>
                                {league.gameCount > 0 && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    ({((league.mafiaWins / league.gameCount) * 100).toFixed(0)}%)
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </MuiGrid>
                          
                          {/* Ties */}
                          {league.tieGames > 0 && (
                            <MuiGrid item xs={4} sm={2} md={2} sx={{ textAlign: 'center' }}>
                              <Box sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                height: '100%',
                                mx: "15px"
                              }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Ties
                                </Typography>
                                <Typography variant="h5" fontWeight="medium">
                                  {league.tieGames}
                                </Typography>
                              </Box>
                            </MuiGrid>
                          )}
                        </MuiGrid>
                      </Box>
                    </ListItem>
                    {index < leagues.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No leagues found.
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default Leagues;